import { createTrackingConfig } from '../../components/generic/labels';
import { tableConfig, uiConfig } from '../../config/appConfig';

export const todoConfig = {
  entityName: 'Todo',
  entityTypeId: 11,

  // Backend API Configuration
  api: {
    baseEndpoint: '/todo',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`
    },
    lookupEndpoints: {
      users: '/users'
    },
    swrInvalidation: {
      mainList: '/todo',
      fileAttachments: (entityTypeId) => `/file-attachment/entity/${entityTypeId}/*`
    }
  },

  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: uiConfig.defaultDrawerWidth,
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    splitView: true,
    tableVariant: 'default' // 'default' | 'cards' | 
  },

  // Service configuration (reference only for generic service mapping)
  service: {
    name: 'todoService',
    methods: {
      getAll: 'getAllTodos',
      getById: 'getTodoById',
      create: 'createTodo',
      update: 'updateTodo',
      delete: 'deleteTodo',
      validate: 'validateData'
    }
  },

  // Display Configuration
  displayName: 'To-Do Management',
  description: 'Simple to-do list to track tasks',
  icon: 'M5 13l4 4L19 7',

  // Fields to remove from forms before submission (relations/computed)
  fieldsToRemove: [
    'id', 'userId', 'assignedToUser', 'assignedToUsers', 'assignees',
    'createdBy', 'updatedBy', 'createdById', 'updatedById',
    'createdAt', 'updatedAt',
    'fileAttachments', 'fileAttachmentCount',
    'assignedToUserId', // legacy single field — use assignedToUserIds
  ],

  features: {
    fileAttachments: true,
    masterEntityTypeId: 11,
  },

  // UNIFIED FIELD CONFIGURATION - Single source of truth
  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'subject',
      label: 'Subject',
      type: 'text',
      required: true,
      placeholder: 'Enter task subject',
      table: {
        header: 'Subject',
        width: '220px',
        sortable: true,
        cellRenderer: 'lineClamp',
        maxLines: 2
      },
      filterable: true,
      validation: {
        required: true,
        minLength: 1,
        maxLength: 200
      }
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      placeholder: 'What needs to be done?',
      rows: 3,
      table: {
        header: 'Description',
        width: '300px',
        sortable: true,
        cellRenderer: 'lineClamp',
        maxLines: 3
      },
      filterable: true,
      validation: {
        required: true,
        minLength: 1,
        maxLength: 1000
      }
    },
    {
      name: 'taskList',
      label: 'Task List',
      type: 'taskList',
      required: false,
      table: {
        header: 'Open Tasks',
        width: '120px',
        sortable: false,
        cellRenderer: 'todoTaskOpenTotal'
      },
      filterable: false,
      validation: { required: false }
    },
    {
      name: 'completed',
      label: 'Completed',
      type: 'switch',
      defaultValue: false,
      switchLabel: 'Completed',
      labelPosition: 'left',
      table: {
        header: 'Completed',
        width: '120px',
        sortable: true,
        cellRenderer: 'toggleSwitch',
        toggleConfig: {
          onToggle: 'onToggleComplete',
          enabledLabel: 'Complete',
          disabledLabel: 'Incomplete',
          enabledColor: 'green',
          disabledColor: 'gray',
          updateField: 'completed'
        }
      },
      filterable: true,
      filterType: 'boolean'
    },
    {
      name: 'completedAt',
      label: 'Completed At',
      type: 'date',
      required: false,
      disabled: true,
      table: {
        header: 'Completed At',
        width: '140px',
        sortable: true,
        cellRenderer: 'optionalDate',
      },
      filterable: true,
      filterType: 'date',
      validation: { required: false },
    },
    {
      name: 'assignedToUserIds',
      label: 'Assigned To',
      type: 'multiSelect',
      required: false,
      placeholder: 'Select assignee(s)',
      lookupEndpoint: 'users',
      labelField: 'userDisplayName',
      table: {
        header: 'Assigned To',
        width: '240px',
        sortable: false,
        displayField: 'assignedToUsers',
        cellRenderer: 'array',
      },
      filterable: true,
      filterType: 'multiSelect',
      validation: { array: true, required: false },
    },
    {
      name: 'dueDate',
      label: 'Due Date',
      type: 'date',
      required: false,
      table: {
        header: 'Due Date',
        width: '140px',
        sortable: true,
        cellRenderer: 'optionalDate'
      },
      filterable: true,
      validation: { required: false }
    },
    {
      name: 'fileAttachments',
      label: 'File(s)',
      type: 'fileUpload',
      useFormFileUpload2: true,
      multiple: true,
      accept: '.pdf,.doc,.docx,.txt,.md,image/*',
      maxSize: 50 * 1024 * 1024,
      maxFiles: 10,
      placeholder: 'Drop files here or click to browse',
      table: {
        header: 'File(s)',
        width: '70px',
        sortable: false,
        displayField: 'fileAttachmentCount',
        cellRenderer: 'fileCount'
      },
      filterable: false,
      validation: {
        required: false
      }
    },
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => ({
        key: field.table.displayField || field.name,
        header: field.table.header || field.label,
        width: field.table.width || 'auto',
        sortable: field.table.sortable !== false,
        displayField: field.table.displayField || field.name,
        cellRenderer: field.table.cellRenderer || 'default',
        toggleConfig: field.table.toggleConfig, // Include toggleConfig for toggle switches
        originalField: field.name
      }));
    return columns;
  },

  exportFileName: 'qms_todos',

  // Backend field mappings (nested display fields if any)
  fieldMappings: {},

  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Details',
      icon: '📝',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['subject', 'description', 'taskList', 'assignedToUserIds', 'dueDate', 'completed', 'completedAt'],
      fieldSpans: {
        'subject': 'md:col-span-12',
        'description': 'md:col-span-12',
        'taskList': 'md:col-span-12',
        'assignedToUserIds': 'md:col-span-6',
        'dueDate': 'md:col-span-6',
        'completed': 'md:col-span-6',
        'completedAt': 'md:col-span-6',
      }
    },
    {
      title: 'File Attachments',
      icon: '📎',
      layout: 'full',
      fields: ['fileAttachments']
    }
  ],

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('Todo'),

  // Labels and Text Configuration
  labels: {
    singular: 'To-Do',
    plural: 'To-Dos',
    pageTitle: 'To-Do List',
    pageSubtitle: 'Track your tasks',
    searchPlaceholder: 'Search tasks...'
  },

  // Custom handlers (optional; set in page if needed)
  customHandlers: {
    onSave: null,
    onDelete: null,
    onLoad: null,
    onFormOpen: null,
    
    // Custom toggle handler for completion status
    // Note: This handler needs to be connected in the Todo page component
    // Example usage in page:
    // window.todoHandlers = { onToggleComplete: todoConfig.customHandlers.onToggleComplete };
    onToggleComplete: async (todoId, currentStatus, updateFunction) => {
      try {
        const newStatus = !currentStatus;
        
        // Call the update function with the new status
        await updateFunction(todoId, { completed: newStatus });
        
        return {
          success: true,
          message: newStatus ? 'Task marked as completed!' : 'Task marked as incomplete!'
        };
      } catch (error) {
        return {
          success: false,
          message: 'Failed to update task status'
        };
      }
    }
  },

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };

    // Remove fields that should not be sent to backend
    delete transformed.id;
    delete transformed.userId;
    delete transformed.assignedToUser;
    delete transformed.assignedToUsers;
    delete transformed.assignees;
    delete transformed.assignedToUserId;
    delete transformed.createdBy;
    delete transformed.updatedBy;
    delete transformed.createdById;
    delete transformed.updatedById;
    delete transformed.createdAt;
    delete transformed.updatedAt;
    delete transformed.fileAttachments;
    delete transformed.fileAttachmentCount;

    // Normalize boolean for completed
    if (transformed.completed !== undefined) {
      if (typeof transformed.completed === 'string') {
        transformed.completed = transformed.completed === 'true';
      } else {
        transformed.completed = Boolean(transformed.completed);
      }
    }

    // completedAt is set/cleared by the backend when completed changes
    delete transformed.completedAt;

    if (transformed.taskList !== undefined) {
      let normalizedTaskList = [];
      if (Array.isArray(transformed.taskList)) {
        normalizedTaskList = transformed.taskList;
      } else if (typeof transformed.taskList === 'string' && transformed.taskList.trim()) {
        try {
          const parsed = JSON.parse(transformed.taskList);
          normalizedTaskList = Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          normalizedTaskList = [];
        }
      }

      const sanitizedTaskList = normalizedTaskList
        .map((item) => ({
          Task: String(item?.task ?? item?.Task ?? '').trim(),
          IsDone: Boolean(item?.isDone ?? item?.IsDone)
        }))
        .filter((item) => item.Task);

      transformed.taskList = JSON.stringify(sanitizedTaskList);
    }

    if (transformed.assignedToUserIds !== undefined) {
      if (!Array.isArray(transformed.assignedToUserIds)) {
        transformed.assignedToUserIds = [];
      } else {
        transformed.assignedToUserIds = transformed.assignedToUserIds
          .map((id) => parseInt(id, 10))
          .filter((id) => !Number.isNaN(id));
      }
    }

    if (transformed.dueDate !== undefined) {
      if (transformed.dueDate === '' || transformed.dueDate === null) {
        transformed.dueDate = null;
      } else {
        const dueDate = new Date(transformed.dueDate);
        transformed.dueDate = Number.isNaN(dueDate.getTime()) ? null : dueDate.toISOString();
      }
    }

    return transformed;
  },

  // Notification messages
  messages: {
    loading: 'Loading tasks...',
    noData: 'No tasks found. Create your first to-do to get started.',
    createSuccess: 'Task created successfully!',
    updateSuccess: 'Task updated successfully!',
    deleteSuccess: 'Task deleted successfully!',
    createError: 'Failed to create task',
    updateError: 'Failed to update task',
    deleteError: 'Failed to delete task',
    loadError: 'Failed to load tasks',
    validationError: 'Validation failed'
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null
};

export default todoConfig;

